import CheckRounded from "@mui/icons-material/CheckRounded";
import CopyAll from "@mui/icons-material/CopyAll";
import OpenInNew from "@mui/icons-material/OpenInNew";
import SendRounded from "@mui/icons-material/SendRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { green, grey } from "@mui/material/colors";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useBorrowerSubmissionsHook } from "../../../hooks/useBorrowerSubmissionsHook";
import SelectToggleInput from "../../inputs/SelectToggleInput";
import InputWrapper from "../../inputs/shared/InputWrapper";
import BasicModal from "../../modals/BasicModal";
import Txt from "../../typography/Txt";
import Flx from "../../layout/Flx";
import { ForwardToInboxOutlined, SendOutlined } from "@mui/icons-material";

const applicationTypesEnums = {
  LOAN_APP: "Loan Application",
  CREDIT_AUTH: "Credit Authorization",
  BOTH: "Both",
};

const SendApplicationButton = ({ show, size = "large", onClose }) => {
  const [localShow, setLocalShow] = useState(false);

  const [creditAuthUrl, setCreditAuthUrl] = useState(null);
  const [loanAppUrl, setLoanAppUrl] = useState(null);
  const styles = useMemo(() => {
    return {
      divider: { mt: 4 },
      linkColumn: { mt: 1, py: 2, px: 1, gap: 6 },
    };
  }, []);
  const handleOnClose = () => {
    setCreditAuthUrl(null);
    setLoanAppUrl(null);
    if (isFunction(onClose)) {
      onClose();
    }
    setLocalShow(false);
  };

  return (
    <>
      <BasicModal
        size="md"
        autoWidth={isNil(creditAuthUrl) && isNil(loanAppUrl)}
        title={"Generate Application Links"}
        show={localShow || show}
        onClose={handleOnClose}
      >
        <BorrowerSelectLinkGeneration
          setCreditAuthUrl={setCreditAuthUrl}
          setLoanAppUrl={setLoanAppUrl}
        />
        {isNil(creditAuthUrl) && isNil(loanAppUrl) ? null : (
          <>
            <Divider sx={styles.divider} />
            <Flx column sx={styles.linkColumn}>
              <UrlCopyAndPaste
                url={loanAppUrl}
                urlText={"Loan Application Url"}
              />
              <UrlCopyAndPaste
                url={creditAuthUrl}
                urlText={"Credit Authorization Url"}
              />
            </Flx>
          </>
        )}
      </BasicModal>
      <Button
        variant="outlined"
        endIcon={<ForwardToInboxOutlined className="thin" />}
        size={size}
        onClick={() => setLocalShow(true)}
      >
        Send Borrower Applications
      </Button>
    </>
  );
};

const UrlCopyAndPaste = ({ url, urlText }) => {
  const [copied, setCopied] = useState(false);
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
  };

  const styles = useMemo(() => {
    return {
      urlText: { fontSize: "13px" },
      urlTextDescription: { fontSize: "11px" },
      urlBox: {
        p: 1,
        background: grey[100],
        overflow: "hidden",
        position: "relative",
        display: "inline-block",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      urlBoxText: { fontSize: "12px", textOverflow: "ellipsis" },
    };
  }, []);

  if (!url) {
    return null;
  }

  return (
    <Flx gap={1.5}>
      <Flx fw column gap={1}>
        <Flx fw jb ac gap={1} sx={{ mt: 1 }}>
          <Flx column>
            <Txt sx={styles.urlText} bold>
              {urlText}
            </Txt>
            <Txt sx={styles.urlTextDescription}>
              This URL includes metadata that tracks the submission for this
              user. Please do not modify.
            </Txt>
          </Flx>
          <Flx jb ac>
            <Tooltip title="Open in new tab" arrow>
              <IconButton
                role="link"
                href={url}
                color="primary"
                target="_blank"
              >
                <OpenInNew />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={copied ? "Copied To Clipboard" : "Copy To Clipboard"}
              arrow
            >
              <IconButton
                role="link"
                color={copied ? "success" : "primary"}
                onClick={handleCopyToClipboard}
                sx={{ background: copied ? green[50] : "inherit" }}
              >
                {copied ? <CheckRounded /> : <CopyAll />}
              </IconButton>
            </Tooltip>
          </Flx>
        </Flx>
        <Box sx={styles.urlBox}>
          <Txt sx={styles.urlBoxText}>{url}</Txt>
        </Box>
      </Flx>
    </Flx>
  );
};

const BorrowerSelectLinkGeneration = ({ setCreditAuthUrl, setLoanAppUrl }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const borrowers = useMemo(() => loanDrilldown?.borrowers, [loanDrilldown]);

  const [selectedBorrowerId, setSelectedBorrowerId] = useState(null);
  const [applicationType, setApplicationType] = useState(null);

  const { loading, generateDealApplicationLink, generateDealCreditAuthLink } =
    useBorrowerSubmissionsHook();

  const borrowerOptions = useMemo(() => {
    return borrowers?.map((b) => ({
      label: `${b?.firstName} ${b?.lastName}`,
      value: b?._id,
    }));
  }, [borrowers]);

  const applicationTypeOptions = useMemo(
    () => [
      applicationTypesEnums.LOAN_APP,
      applicationTypesEnums.CREDIT_AUTH,
      applicationTypesEnums.BOTH,
    ],
    []
  );

  const handleGenerateLinks = () => {
    const selectedBorrowerData = borrowers?.find(
      (b) => b?._id === selectedBorrowerId
    );

    if (
      applicationType === applicationTypesEnums.BOTH ||
      applicationType === applicationTypesEnums.LOAN_APP
    ) {
      generateDealApplicationLink({
        loanId: loanDrilldown?._id,
        borrowerData: selectedBorrowerData,
        onSuccessFn: (d) => {
          setLoanAppUrl(d?.link);
        },
      });
    }
    if (
      applicationType === applicationTypesEnums.BOTH ||
      applicationType === applicationTypesEnums.CREDIT_AUTH
    ) {
      generateDealCreditAuthLink({
        loanId: loanDrilldown?._id,
        borrowerData: selectedBorrowerData,
        onSuccessFn: (d) => {
          setCreditAuthUrl(d?.link);
        },
      });
    }
  };

  return (
    <Flx fw column gap={3} jb>
      <Flx gap={8}>
        <InputWrapper label="Select Borrower" sx={{ minWidth: "300px" }}>
          <SelectToggleInput
            value={selectedBorrowerId}
            onChange={setSelectedBorrowerId}
            options={borrowerOptions}
            placeholder="Select Borrower"
          />
        </InputWrapper>
        <InputWrapper label="Choose Application Type">
          <SelectToggleInput
            value={applicationType}
            onChange={setApplicationType}
            options={applicationTypeOptions}
            placeholder="Select Borrower"
          />
        </InputWrapper>
      </Flx>
      <Flx end fw>
        <Button
          variant="contained"
          loading={loading}
          disabled={
            loading || isNil(selectedBorrowerId) || isNil(applicationType)
          }
          endIcon={<SendRounded />}
          onClick={handleGenerateLinks}
        >
          {applicationType === applicationTypesEnums.BOTH
            ? "Generate Links"
            : "Generate Link"}
        </Button>
      </Flx>
    </Flx>
  );
};
export default SendApplicationButton;
